import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { DbTemplateService } from '../db-template.service';
import { DB_FIELD_DATA_TYPES, DB_FIELD_DATA_TYPES_WITH_LENGTH, DbTemplateItem } from '../db-template.model';

/**
 * "Opret felt" på en stored procedure-skabelon: en resultatkolonne, som sp_describe_first_result_set ikke ser
 * (fordi proceduren returnerer forskellige kolonner afhængigt af IF/CASE). Feltet oprettes som SPHybrid og er
 * kun læsbart. Kolonne og vist navn må ikke findes i forvejen — det tjekkes her og igen på serveren.
 */
@Component({
  selector: 'app-db-template-field-create',
  templateUrl: './db-template-field-create.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DbTemplateFieldCreateComponent {
  readonly activeModal = inject(NgbActiveModal);
  private readonly service = inject(DbTemplateService);
  private readonly toastr = inject(ToastrService);

  // Sættes af den, der åbner modalen (NgbModal sætter ikke signal-inputs).
  readonly dbTemplateId = signal('');
  readonly existingItems = signal<DbTemplateItem[]>([]);

  readonly dataTypes = DB_FIELD_DATA_TYPES;

  readonly columnName = signal('');
  readonly name = signal('');
  readonly dataType = signal('nvarchar');
  readonly maxLength = signal<number | null>(50);
  readonly isMax = signal(false);
  readonly isNullable = signal(true);
  readonly dbDefaultValue = signal('');
  readonly saving = signal(false);

  readonly hasLength = computed(() => DB_FIELD_DATA_TYPES_WITH_LENGTH.includes(this.dataType()));
  /** Vist navn er kolonnenavnet, hvis det ikke udfyldes. */
  readonly effectiveName = computed(() => this.name().trim() || this.columnName().trim());

  readonly columnError = computed(() => {
    const column = this.columnName().trim();
    if (!column) {
      return 'Kolonne skal udfyldes.';
    }
    if (column.includes('[') || column.includes(']')) {
      return 'Kolonne må ikke indeholde [ eller ].';
    }
    if (this.existingItems().some(x => x.dbColumnName.toLowerCase() == column.toLowerCase())) {
      return `Kolonnen '${column}' findes allerede i skabelonen.`;
    }
    return null;
  });

  readonly nameError = computed(() => {
    const name = this.effectiveName();
    if (name && this.existingItems().some(x => (x.name ?? '').toLowerCase() == name.toLowerCase())) {
      return `Vist navn '${name}' findes allerede i skabelonen.`;
    }
    return null;
  });

  readonly lengthError = computed(() => {
    if (!this.hasLength() || this.isMax()) {
      return null;
    }
    const length = this.maxLength();
    return length == null || !(length > 0) ? 'Max. længde skal være større end 0.' : null;
  });

  readonly valid = computed(() => !this.columnError() && !this.nameError() && !this.lengthError());

  /** Fejl vises først, når feltet er rørt, så dialogen ikke åbner i rødt. */
  readonly touched = signal(new Set<string>());

  touch(field: string): void {
    this.touched.update(x => new Set(x).add(field));
  }

  showError(field: string, error: string | null): string | null {
    return this.touched().has(field) ? error : null;
  }

  text(event: Event): string {
    return (event.target as HTMLInputElement).value;
  }

  checked(event: Event): boolean {
    return (event.target as HTMLInputElement).checked;
  }

  setMaxLength(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.maxLength.set(value === '' ? null : Number(value));
  }

  async create(): Promise<void> {
    ['column', 'name', 'length'].forEach(x => this.touch(x));
    if (!this.valid()) {
      return;
    }

    this.saving.set(true);
    try {
      const id = await this.service.createDbTemplateField(this.dbTemplateId(), {
        columnName: this.columnName().trim(),
        name: this.effectiveName(),
        dataType: this.dataType(),
        maxLength: this.hasLength() ? (this.isMax() ? -1 : this.maxLength()) : null,
        isNullable: this.isNullable(),
        dbDefaultValue: this.dbDefaultValue().trim() || null
      });
      this.toastr.success(`Feltet '${this.effectiveName()}' er oprettet`);
      this.activeModal.close(id);
    } catch {
      // HTTP-fejl vises globalt af GlobalErrorInterceptor/ErrorService
    } finally {
      this.saving.set(false);
    }
  }
}
